// ─── SEO metadata for every page ───────────────────────────────────
// Each entry maps a route to its <title>, meta description, and Open Graph tags.
// The keywords are written for the primary search intent of that tool — the
// thing a student would actually type into Google.
//
// Best practices baked in:
//   - title: ~50-60 chars, primary keyword first, brand at end
//   - description: ~150-160 chars, includes 2-3 supporting keywords, ends actionably
//   - og:image points to the tool's icon for nice social previews
//
// Edit any line here to change how the tool appears in Google results. No
// other files need to change.

// Base URL — used for canonical URLs and absolute og:image references.
// IMPORTANT: update this after deploying to your real domain.
const SITE_URL = 'https://nerdsuite.app';

export const SEO = {
  '/': {
    title: 'NerdStack — Free Study Tools for Students',
    description: 'Eight free study tools for students: GPA calculator, Pomodoro timer, word counter, percentage and unit converters, and more. Free. No signup. No dark patterns.',
    keywords: 'study tools, free study tools, student tools, GPA calculator, Pomodoro timer, word counter, unit converter, percentage calculator, study apps',
    ogImage: '/icons/icon-512.png',
  },

  '/gradecalc': {
    title: 'GradeCalc — Free GPA & Final Grade Calculator',
    description: 'Calculate your weighted GPA, figure out what you need on your final, and plan your semester. Supports AP, Honors, IB, and Dual Enrollment. Free, no signup.',
    keywords: 'GPA calculator, grade calculator, final grade calculator, weighted GPA, AP weighted GPA, cumulative GPA, semester planner, college grade calculator',
    ogImage: '/icons/gradecalc-256.png',
  },

  '/timer': {
    title: 'StudyTimer — Free Pomodoro Timer for Students',
    description: 'Free Pomodoro study timer with focus sounds (white noise, brown noise, rain). Track sessions, build streaks, study smarter. No signup required.',
    keywords: 'Pomodoro timer, study timer, focus timer, free Pomodoro, 25 minute timer, study sessions, focus sounds, white noise study',
    ogImage: '/icons/timer-256.png',
  },

  '/wordcount': {
    title: 'WordCount — Free Word & Character Counter for Essays',
    description: 'Count words, characters, sentences, and paragraphs. Estimate reading time and grade level. Upload PDF, DOCX, or PPTX — processed in your browser, never uploaded.',
    keywords: 'word counter, character counter, free word count, essay word counter, reading time, words per minute, PDF word count, DOCX word count',
    ogImage: '/icons/wordcount-256.png',
  },

  '/percent': {
    title: 'PercentCalc — Free Percentage Calculator',
    description: 'Calculate percentages quickly: what is X% of Y, percent change, percent off, tip and tax math, and more. Free, no signup, works offline.',
    keywords: 'percentage calculator, percent calculator, percent change, discount calculator, tip calculator, percentage of a number, percent off',
    ogImage: '/icons/percentcalc-256.png',
  },

  '/convert': {
    title: 'UnitConvert — Free Unit Converter for Students',
    description: 'Convert between units in 18 categories: length, mass, volume, temperature, energy, chemistry (molecular weight), physics, and more. Free, instant, accurate.',
    keywords: 'unit converter, free unit converter, metric converter, imperial converter, temperature converter, mass converter, chemistry units, mol calculator',
    ogImage: '/icons/unitconvert-256.png',
  },

  '/average': {
    title: 'AverageCalc — Free Statistics Calculator',
    description: 'Calculate mean, median, mode, range, variance, and standard deviation. Show step-by-step work. Free statistics calculator for students. No signup.',
    keywords: 'average calculator, mean calculator, median calculator, statistics calculator, standard deviation calculator, variance calculator, statistics for students',
    ogImage: '/icons/averagecalc-256.png',
  },

  '/quizcard': {
    title: 'QuizCard — AI Flashcards from Your Notes (Coming Soon)',
    description: 'Turn your notes into flashcards and quizzes with AI. Launching soon. Until then, explore six free study tools at NerdStack.',
    keywords: 'AI flashcards, flashcard generator, notes to flashcards, AI study tool, study generator, AI quiz generator',
    ogImage: '/icons/quizcard-256.png',
  },

  '/clearlens': {
    title: 'ClearLens — AI Concept Explainer (Coming Soon)',
    description: 'Confusing concepts, explained clearly with AI. Launching soon. Until then, explore six free study tools at NerdStack.',
    keywords: 'AI tutor, concept explainer, AI study help, explain like im five, AI homework help, study AI',
    ogImage: '/icons/clearlens-256.png',
  },

  '/chem': {
    title: 'NerdChem — Free Chemistry Tools & Calculators for Students',
    description: 'Free chemistry tools: interactive periodic table, plus molecular weight, mole, equation balancer and more on the way. Free. No signup. No tracking. No data sale.',
    keywords: 'chemistry tools, free chemistry calculator, periodic table, molecular weight calculator, mole calculator, chemistry calculator for students, NerdChem',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/home': {
    title: 'NerdChem — Free Chemistry Tools & Calculators for Students',
    description: 'NerdChem tools — periodic table, molecular weight, mole calculator, equation balancer, pH, dilution, sig figs, gas laws and stoichiometry. Free, no signup.',
    keywords: 'chemistry tools, free chemistry calculator, periodic table, molecular weight calculator, mole calculator, chemistry calculator for students, NerdChem',
    ogImage: '/icons/icon-512.png',
  },


  '/chem/periodic-table': {
    title: 'Periodic Table — Free Interactive Periodic Table | NerdChem',
    description: 'Interactive periodic table of all 118 elements. Tap any element for atomic mass, electron configuration, state, density, melting/boiling points. Free, no signup.',
    keywords: 'periodic table, interactive periodic table, periodic table of elements, elements, atomic mass, electron configuration, periodic table app, free periodic table',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/molecular-weight': {
    title: 'Molecular Weight Calculator — Free Molar Mass Tool | NerdChem',
    description: 'Calculate molar mass from any chemical formula. Handles parentheses, polyatomic ions, and hydrates. C6H12O6 → 180.16 g/mol. Free, instant, no signup.',
    keywords: 'molecular weight calculator, molar mass calculator, molecular mass, formula weight, gram molecular mass, chemistry calculator',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/mole-calculator': {
    title: 'Mole Calculator — Grams, Moles & Molecules | NerdChem',
    description: 'Convert between grams, moles, and number of molecules for any compound. Enter a formula and one value, get the rest. Free mole calculator, no signup.',
    keywords: 'mole calculator, moles to grams, grams to moles, moles to molecules, Avogadro number calculator, mole conversion',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/ph-calculator': {
    title: 'pH Calculator — pH, pOH, [H+] and [OH-] | NerdChem',
    description: 'Enter any one of pH, pOH, hydrogen ion or hydroxide concentration and get the other three. Free pH calculator for chemistry students. No signup.',
    keywords: 'pH calculator, pOH calculator, hydrogen ion concentration, hydroxide concentration, pH to H+, acid base calculator',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/dilution': {
    title: 'Dilution Calculator — M1V1 = M2V2 Solver | NerdChem',
    description: 'Solve the dilution equation for any unknown. Enter any three of initial/final concentration and volume, get the fourth. Free, instant, no signup.',
    keywords: 'dilution calculator, M1V1 M2V2, solution dilution, concentration calculator, stock solution dilution, molarity dilution',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/sig-figs': {
    title: 'Significant Figures Calculator — Count & Round | NerdChem',
    description: 'Count significant figures in any number and round to a chosen number of sig figs. Supports decimals and scientific notation. Free, no signup.',
    keywords: 'significant figures calculator, sig fig calculator, sig figs counter, rounding significant figures, sig fig rules',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/equation-balancer': {
    title: 'Chemical Equation Balancer — Free & Exact | NerdChem',
    description: 'Balance any chemical equation instantly with exact whole-number coefficients. Handles redox and complex reactions. Free. No signup. No tracking. No data sale.',
    keywords: 'equation balancer, balance chemical equation, chemical equation balancer, balancing equations, stoichiometric coefficients, redox balancer',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/gas-laws': {
    title: 'Gas Law Solver — Ideal & Combined Gas Law | NerdChem',
    description: 'Solve PV = nRT and the combined gas law for any variable. Covers Boyle, Charles, Gay-Lussac and ideal gas problems. Free, instant, no signup.',
    keywords: 'gas law calculator, ideal gas law, PV=nRT calculator, combined gas law, Boyle law, Charles law, Gay-Lussac, gas law solver',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/stoichiometry': {
    title: 'Stoichiometry Calculator — Limiting Reagent & Yield | NerdChem',
    description: 'Find the limiting reagent and theoretical yield for any reaction. Auto-balances the equation, accepts grams or moles. Free, exact, no signup.',
    keywords: 'stoichiometry calculator, limiting reagent calculator, theoretical yield calculator, limiting reactant, mole ratio, reaction yield',
    ogImage: '/icons/icon-512.png',
  },

  '/math': {
    title: 'NerdMath — Free Math Tools & Calculators for Students',
    description: 'Free math tools: scientific calculator, quadratic solver with full worked steps, trig reference, logarithm and matrix tools. No tracking. No signup. No dark patterns.',
    keywords: 'math tools, free math calculator, scientific calculator, quadratic solver, matrix calculator, trig calculator, logarithm calculator, NerdMath',
    ogImage: '/icons/icon-512.png',
  },

  '/math/home': {
    title: 'NerdMath — Free Math Tools & Calculators for Students',
    description: 'NerdMath tools — scientific calculator, quadratic solver, trigonometry, logarithms and matrix operations. Free. No tracking. No signup. No dark patterns.',
    keywords: 'math tools, free math calculator, scientific calculator, quadratic solver, matrix calculator, trig calculator, logarithm calculator, NerdMath',
    ogImage: '/icons/icon-512.png',
  },

  '/math/calculator': {
    title: 'Scientific Calculator — Free Online | NerdMath',
    description: 'Full scientific calculator: trig, logs, powers, roots, factorial, constants. Free, fast, no signup, works offline once installed.',
    keywords: 'scientific calculator, online calculator, free scientific calculator, trig calculator, factorial calculator, expression evaluator',
    ogImage: '/icons/icon-512.png',
  },
  '/math/quadratic': {
    title: 'Quadratic Formula Solver — Free with Steps | NerdMath',
    description: 'Solve any quadratic equation with full worked steps shown free — no paywall. Handles real, repeated, and complex roots. No signup.',
    keywords: 'quadratic formula solver, quadratic equation calculator, quadratic with steps, discriminant calculator, complex roots',
    ogImage: '/icons/icon-512.png',
  },
  '/math/trig': {
    title: 'Trig & Unit Circle Reference — Free | NerdMath',
    description: 'Exact and decimal values of sin, cos, tan for any angle, plus the unit circle. Free trigonometry reference for students. No signup.',
    keywords: 'unit circle, trig calculator, sine cosine tangent values, exact trig values, trigonometry reference, unit circle chart',
    ogImage: '/icons/icon-512.png',
  },
  '/math/logarithm': {
    title: 'Logarithm Calculator — Any Base, Free | NerdMath',
    description: 'Calculate logarithms in any base, natural log (ln), and common log. Free logarithm calculator for students. No tracking. No signup. No dark patterns.',
    keywords: 'logarithm calculator, log base calculator, natural log calculator, ln calculator, log any base',
    ogImage: '/icons/icon-512.png',
  },
  '/math/matrix': {
    title: 'Matrix Calculator — Determinant, Inverse, Solver | NerdMath',
    description: 'Compute determinant and inverse, and solve linear systems Ax = b for 2×2 to 4×4 matrices. Free matrix calculator for students. No signup.',
    keywords: 'matrix calculator, determinant calculator, matrix inverse, solve linear system, Ax=b solver, linear algebra calculator',
    ogImage: '/icons/icon-512.png',
  },

  '/phys': {
    title: 'NerdPhys — Free Physics Tools & Calculators for Students',
    description: 'Free physics tools: projectile motion with trajectory plot, SUVAT kinematics solver, Newton\u2019s 2nd law, energy & work, Ohm\u2019s law. Free. No signup. No dark patterns.',
    keywords: 'physics tools, projectile motion calculator, SUVAT solver, kinematics calculator, Newton second law, Ohms law calculator, NerdPhys',
    ogImage: '/icons/icon-512.png',
  },

  '/phys/home': {
    title: 'NerdPhys — Free Physics Tools & Calculators for Students',
    description: 'NerdPhys tools — projectile motion with trajectory plot, SUVAT kinematics, Newton\u2019s second law, energy & work, and Ohm\u2019s law. Free, no signup.',
    keywords: 'physics tools, projectile motion calculator, SUVAT solver, kinematics calculator, Newton second law, Ohms law calculator, NerdPhys',
    ogImage: '/icons/icon-512.png',
  },

  '/phys/projectile': {
    title: 'Projectile Motion Calculator — With Trajectory Plot | NerdPhys',
    description: 'Calculate range, flight time, max height and impact speed for projectile motion, with a live trajectory plot. Ideal no-air-resistance model. Free.',
    keywords: 'projectile motion calculator, trajectory plot, range calculator, max height projectile, flight time, physics projectile',
    ogImage: '/icons/icon-512.png',
  },
  '/phys/kinematics': {
    title: 'SUVAT Kinematics Solver — Free with Steps | NerdPhys',
    description: 'Solve constant-acceleration motion from any 3 of u, v, a, t, s. Shows equations used and both physical solutions when an input is ambiguous. Free.',
    keywords: 'SUVAT solver, kinematics calculator, constant acceleration, equations of motion, u v a t s solver, kinematics steps',
    ogImage: '/icons/icon-512.png',
  },
  '/phys/force': {
    title: 'Newton\u2019s Second Law Calculator (F = ma) | NerdPhys',
    description: 'Solve F = ma for force, mass, or acceleration. Free Newton\u2019s second law calculator for physics students. Free. No signup. No dark patterns.',
    keywords: 'F=ma calculator, Newton second law, force mass acceleration, newton calculator, physics force calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/phys/energy': {
    title: 'Energy & Work Calculator — KE, PE, Work | NerdPhys',
    description: 'Calculate kinetic energy, potential energy, and work done including angle. Free energy and work calculator for physics students. No signup.',
    keywords: 'kinetic energy calculator, potential energy calculator, work done calculator, KE PE work, energy physics calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/phys/ohms': {
    title: 'Ohm\u2019s Law Calculator — V = IR and Power | NerdPhys',
    description: 'Solve Ohm\u2019s law for voltage, current, or resistance, plus electrical power. Free Ohm\u2019s law calculator for students. Free. No signup. No dark patterns.',
    keywords: 'ohms law calculator, V=IR calculator, voltage current resistance, electrical power calculator, ohms law solver',
    ogImage: '/icons/icon-512.png',
  },

  '/tools': {
    title: 'NerdTools — Free Device Utilities: Converter, Level, QR & More',
    description: 'Free device tools: unit converter, bubble level, compass, decibel meter, screen ruler, QR generator. No tracking. No signup. No dark patterns. Works in your browser.',
    keywords: 'unit converter, bubble level, compass app, decibel meter, screen ruler, QR code generator, free online tools, NerdTools',
    ogImage: '/icons/icon-512.png',
  },

  '/tools/home': {
    title: 'NerdTools — Free Device Utilities: Converter, Level, QR & More',
    description: 'NerdTools utilities — unit converter, bubble level, compass, decibel meter, screen ruler and QR code generator. Free. No tracking. No signup. No dark patterns.',
    keywords: 'unit converter, bubble level, compass app, decibel meter, screen ruler, QR code generator, free online tools, NerdTools',
    ogImage: '/icons/icon-512.png',
  },

  '/tools/converter': {
    title: 'Unit Converter — Length, Mass, Volume, Speed & More | NerdTools',
    description: 'Free, accurate unit converter: length, mass, volume, speed, area, data storage, and temperature. NIST/SI-exact constants. No signup.',
    keywords: 'unit converter, measurement converter, metric imperial converter, temperature converter, length mass volume converter',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/compass': {
    title: 'Compass — Free Digital Compass for Your Phone | NerdTools',
    description: 'A simple digital compass using your phone\u2019s magnetometer. Free, no signup. Calibrate with a figure-8 motion for best accuracy.',
    keywords: 'compass app, digital compass, phone compass, magnetic heading, direction finder',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/decibel': {
    title: 'Decibel Meter — Relative Sound Level | NerdTools',
    description: 'Measure relative sound level with your phone microphone. Honestly labeled relative (not calibrated SPL). Free, no signup.',
    keywords: 'decibel meter, sound level meter, noise meter, dB meter, microphone sound measurement',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/ruler': {
    title: 'Screen Ruler — Calibrated On-Screen Ruler | NerdTools',
    description: 'An on-screen ruler you calibrate with a credit card for accuracy. Measure small objects in cm or inches. Free, no signup.',
    keywords: 'screen ruler, on-screen ruler, digital ruler, measure on screen, cm inch ruler',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/qr': {
    title: 'QR Code Generator — Free, No Signup, No Ads | NerdTools',
    description: 'Generate a QR code for any link or text and download it as a PNG. Free, no signup, no tracking. Codes verified scannable.',
    keywords: 'QR code generator, free QR code, QR maker, generate QR code, QR code PNG download',
    ogImage: '/icons/icon-512.png',
  },

  '/bio': {
    title: 'NerdBio — Free Genetics & Molecular Biology Tools',
    description: 'Free biology tools: DNA/RNA toolkit, codon translator, Punnett squares, Hardy-Weinberg, lab molarity, and a full anatomy reference with quiz. Free. No signup. No dark patterns.',
    keywords: 'biology tools, DNA tools, codon translator, Punnett square calculator, Hardy-Weinberg calculator, genetics calculator, NerdBio',
    ogImage: '/icons/icon-512.png',
  },

  '/bio/home': {
    title: 'NerdBio — Free Genetics & Molecular Biology Tools',
    description: 'NerdBio tools — DNA toolkit, codon translator, Punnett squares, Hardy-Weinberg, lab molarity/dilution and a full anatomy reference with quiz. Free, no signup.',
    keywords: 'biology tools, DNA tools, codon translator, Punnett square calculator, Hardy-Weinberg calculator, genetics calculator, NerdBio',
    ogImage: '/icons/icon-512.png',
  },

  '/bio/dna': {
    title: 'DNA Toolkit — Transcribe, Complement, GC% | NerdBio',
    description: 'Transcribe DNA to mRNA, get complement and reverse-complement, and GC content. Free DNA toolkit for biology students. No signup.',
    keywords: 'DNA toolkit, transcribe DNA, reverse complement, complement strand, GC content calculator, mRNA',
    ogImage: '/icons/icon-512.png',
  },
  '/bio/translate': {
    title: 'Codon Translator — DNA to Protein | NerdBio',
    description: 'Translate a DNA sequence to protein using the standard genetic code, in any of 3 reading frames. Free codon translator for students. No signup.',
    keywords: 'codon translator, DNA to protein, genetic code table, reading frame translation, amino acid translator',
    ogImage: '/icons/icon-512.png',
  },
  '/bio/punnett': {
    title: 'Punnett Square Calculator — Mono & Dihybrid | NerdBio',
    description: 'Generate monohybrid and dihybrid Punnett squares with genotype and phenotype ratios. Free Punnett square calculator for students. No signup.',
    keywords: 'Punnett square calculator, monohybrid cross, dihybrid cross, genotype phenotype ratio, Mendelian genetics',
    ogImage: '/icons/icon-512.png',
  },
  '/bio/hardy-weinberg': {
    title: 'Hardy-Weinberg Calculator — Allele Frequencies | NerdBio',
    description: 'Compute genotype frequencies from p, or p and q from the recessive frequency, using the Hardy-Weinberg equation. Free, for students. No signup.',
    keywords: 'Hardy-Weinberg calculator, allele frequency, genotype frequency, population genetics, p q equilibrium',
    ogImage: '/icons/icon-512.png',
  },
  '/bio/lab': {
    title: 'Lab Molarity & Dilution Calculator | NerdBio',
    description: 'Solve molarity (moles/liters) and C\u2081V\u2081 = C\u2082V\u2082 dilution for the missing value. Free lab calculator for biology students. No signup.',
    keywords: 'molarity calculator, dilution calculator, C1V1 C2V2, moles per liter, lab solution calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/bio/anatomy': {
    title: 'Anatomy Reference — Systems, Nerves, Bones & Muscles | NerdBio',
    description: 'Anatomy study reference: organ systems, cranial & spinal nerves, vessels, the skeleton, muscles, endocrine glands, heart, brain, tissues and joints — with a built-in quiz. Factual reference, not medical guidance. Free, no signup.',
    keywords: 'anatomy reference, cranial nerves, spinal nerves, skeletal system, muscle anatomy, brachial plexus, endocrine glands, anatomy quiz, anatomy study guide, biology anatomy',
    ogImage: '/icons/icon-512.png',
  },

  '/phys/reference': {
    title: 'Physics Reference — Kinematics through Quantum Field Theory | NerdPhys',
    description: 'Physics study reference: kinematics, dynamics, energy, momentum, rotation, waves, electromagnetism, thermodynamics, optics, modern physics, particles, plus advanced tabs for classical mechanics, general relativity, and quantum field theory. 376 items with formulas, conditions, and worked examples. Free, no signup.',
    keywords: 'physics reference, kinematics formulas, dynamics formulas, projectile motion, energy conservation, momentum, rotation, electromagnetism, thermodynamics, optics, modern physics reference, general relativity reference, quantum field theory, physics study guide',
    ogImage: '/icons/icon-512.png',
  },

  '/chem/reference': {
    title: 'Chemistry Reference — Concepts, Reactions, Procedures, Trends | NerdChem',
    description: 'Chemistry study reference: 135 items across concepts (Le Chatelier, equilibrium, oxidation), reactions (synthesis, redox, neutralization), procedures (mole conversions, stoichiometry), lookup tables (polyatomic ions, solubility), periodic trends, and equations (PV=nRT, Henderson-Hasselbalch). Free, no signup.',
    keywords: 'chemistry reference, periodic trends, chemistry reactions, mole conversions, stoichiometry, polyatomic ions, solubility rules, Le Chatelier, ideal gas law, Henderson-Hasselbalch, chemistry study guide',
    ogImage: '/icons/icon-512.png',
  },

  '/bio/reference': {
    title: 'Biology Reference — Cells, Genetics, Evolution, Ecology, Physiology | NerdBio',
    description: 'Biology study reference: 274 items across cell biology (organelles, transport, cell cycle), genetics (DNA, transcription, translation, inheritance), evolution (selection, speciation, phylogeny), ecology (biomes, populations, communities), biochemistry, physiology, plants, and diversity — plus advanced tier for molecular biology, immunology, development, neuroscience, and biostats. Companion to the Anatomy Reference. Free. No signup. No dark patterns.',
    keywords: 'biology reference, cell biology, genetics, evolution, ecology, physiology, molecular biology, immunology, neuroscience, biostatistics, biology study guide, AP biology, college biology',
    ogImage: '/icons/icon-512.png',
  },

  '/math/reference': {
    title: 'Math Reference — Algebra, Calculus, Linear Algebra, Statistics & More | NerdMath',
    description: 'Math study reference: 418 items across algebra, geometry, trigonometry, statistics, calculus, linear algebra, discrete math, and differential equations — plus advanced tier for abstract algebra, real and complex analysis, topology, measure-theoretic probability, and PDEs. Theorems, formulas, worked examples, common mistakes. No tracking. No signup. No dark patterns.',
    keywords: 'math reference, calculus reference, linear algebra, statistics, algebra, trigonometry, differential equations, real analysis, topology, math study guide, AP calculus, college math',
    ogImage: '/icons/icon-512.png',
  },

  '/music': {
    title: 'NerdMusic — Free Music Theory Reference for Students',
    description: 'NerdMusic — music theory reference: notation, scales, intervals, chords, harmony, rhythm, form, ear training, voice leading, jazz, analysis, tuning. Free. No signup. No dark patterns.',
    keywords: 'music theory, scales, chords, intervals, harmony, ear training, voice leading, jazz theory, music reference, free music theory',
    ogImage: '/icons/icon-512.png',
  },

  '/music/reference': {
    title: 'Music Theory Reference — Scales, Chords, Harmony, Ear Training & More | NerdMusic',
    description: 'Music theory reference: 307 items across notation, scales, intervals, chords, harmony, rhythm, form, and ear training — plus advanced tier for voice leading, chromatic harmony, 20th-century theory, jazz, analysis, and tuning. Sound descriptions, worked examples, common mistakes. Free. No signup. No dark patterns.',
    keywords: 'music theory reference, scales reference, chord reference, harmony reference, ear training, voice leading, jazz theory, music tuning, intervals, cadences, music notation, music study guide',
    ogImage: '/icons/icon-512.png',
  },

  '/history': {
    title: 'NerdHist — Free History Reference for Students',
    description: 'NerdHist — world history reference: ancient, medieval, early modern, modern, 20th century, US history, plus historiography, methods, intellectual / economic / military / science threads. Free. No signup. No dark patterns.',
    keywords: 'history reference, world history, US history, historiography, ancient history, medieval history, early modern history, modern history, 20th century, free history study',
    ogImage: '/icons/icon-512.png',
  },

  '/history/reference': {
    title: 'History Reference — Ancient through 20th Century, plus Methods & Themes | NerdHist',
    description: 'History study reference: 333 items across ancient, medieval, early modern, modern, 20th century, and US history — plus advanced tier for historiography, methods, intellectual, economic, military & diplomatic, and science history. Events, people, ideas, institutions, periods. Free. No signup. No dark patterns.',
    keywords: 'history reference, world history, US history, historiography, history methods, ancient history, medieval history, early modern, modern history, 20th century history, history study guide, AP history, college history',
    ogImage: '/icons/icon-512.png',
  },

  '/suggest': {
    title: 'Suggest a Tool — NerdStack',
    description: "Got a study tool you wish existed? Tell us. We're listening, and the best ideas get built first.",
    keywords: 'NerdStack tools, study tool requests, student tool ideas',
    ogImage: '/icons/icon-512.png',
  },

  // ── Physics: 3 pages that were missing entries (served generic homepage SEO) ──
  '/phys/momentum': {
    title: 'Momentum & Impulse Calculator — p = mv | NerdPhys',
    description: 'Calculate momentum, impulse, and solve collision problems (elastic and inelastic). Free momentum calculator for physics students. Free. No signup. No dark patterns.',
    keywords: 'momentum calculator, impulse calculator, p=mv, conservation of momentum, collision calculator, elastic inelastic collision, physics momentum',
    ogImage: '/icons/icon-512.png',
  },

  '/phys/particles': {
    title: 'Particle Physics Reference — Quarks, Leptons, Bosons | NerdPhys',
    description: 'Standard Model reference: quarks, leptons, gauge bosons, their charges, spin, and antiparticles, with a built-in quiz. Free, no signup.',
    keywords: 'particle physics, standard model, quarks leptons bosons, elementary particles, antiparticles, particle physics quiz, physics reference',
    ogImage: '/icons/icon-512.png',
  },

  '/phys/waves': {
    title: 'Wave Calculator — Frequency, Wavelength, Speed | NerdPhys',
    description: 'Solve the wave equation for speed, frequency, wavelength, and period. Free wave calculator for physics students. Free. No signup. No dark patterns.',
    keywords: 'wave calculator, wave equation, frequency wavelength speed, v=fλ, period frequency, wave speed calculator, physics waves',
    ogImage: '/icons/icon-512.png',
  },

  // ── /stack canonical pages: the REAL pages (/stack/x). seo.js previously
  //    only had the redirect twins (/x), so the canonical pages served
  //    generic homepage SEO. Same proven copy, re-keyed to the canonical path. ──
  '/stack': {
    title: 'NerdStack — Free Study Tools for Students',
    description: 'Eight free study tools for students: GPA calculator, Pomodoro timer, word counter, percentage and unit converters, and more. Free. No signup. No tracking. No data sale.',
    keywords: 'study tools, free study tools, student tools, GPA calculator, Pomodoro timer, word counter, unit converter, percentage calculator, study apps',
    ogImage: '/icons/icon-512.png',
  },

  '/stack/gradecalc': {
    title: 'GradeCalc — Free GPA & Final Grade Calculator',
    description: 'Calculate your weighted GPA, figure out what you need on your final, and plan your semester. Supports AP, Honors, IB, and Dual Enrollment. Free, no signup.',
    keywords: 'GPA calculator, grade calculator, final grade calculator, weighted GPA, AP weighted GPA, cumulative GPA, semester planner, college grade calculator',
    ogImage: '/icons/gradecalc-256.png',
  },

  '/stack/timer': {
    title: 'StudyTimer — Free Pomodoro Timer for Students',
    description: 'Free Pomodoro study timer with focus sounds (white noise, brown noise, rain). Track sessions, build streaks, study smarter. No signup required.',
    keywords: 'Pomodoro timer, study timer, focus timer, free Pomodoro, 25 minute timer, study sessions, focus sounds, white noise study',
    ogImage: '/icons/timer-256.png',
  },

  '/stack/wordcount': {
    title: 'WordCount — Free Word & Character Counter for Essays',
    description: 'Count words, characters, sentences, and paragraphs. Estimate reading time and grade level. Upload PDF, DOCX, or PPTX — processed in your browser, never uploaded.',
    keywords: 'word counter, character counter, free word count, essay word counter, reading time, words per minute, PDF word count, DOCX word count',
    ogImage: '/icons/wordcount-256.png',
  },

  '/stack/percent': {
    title: 'PercentCalc — Free Percentage Calculator',
    description: 'Calculate percentages quickly: what is X% of Y, percent change, percent off, tip and tax math, and more. Free, no signup, works offline.',
    keywords: 'percentage calculator, percent calculator, percent change, discount calculator, tip calculator, percentage of a number, percent off',
    ogImage: '/icons/percentcalc-256.png',
  },

  '/stack/convert': {
    title: 'UnitConvert — Free Unit Converter for Students',
    description: 'Convert between units in 18 categories: length, mass, volume, temperature, energy, chemistry (molecular weight), physics, and more. Free, instant, accurate.',
    keywords: 'unit converter, free unit converter, metric converter, imperial converter, temperature converter, mass converter, chemistry units, mol calculator',
    ogImage: '/icons/unitconvert-256.png',
  },

  '/stack/average': {
    title: 'AverageCalc — Free Statistics Calculator',
    description: 'Calculate mean, median, mode, range, variance, and standard deviation. Show step-by-step work. Free statistics calculator for students. No signup.',
    keywords: 'average calculator, mean calculator, median calculator, statistics calculator, standard deviation calculator, variance calculator, statistics for students',
    ogImage: '/icons/averagecalc-256.png',
  },

  // ── NerdGames suite landing + legal (were missing entries) ──
  '/games': {
    title: 'NerdGames — Free Learning Games for Students',
    description: 'Free leveled mastery games: master the periodic table, genetic code, anatomy, the unit circle, and number sense by depth — one stage at a time. No signup.',
    keywords: 'learning games, study games, free educational games, periodic table game, unit circle practice, anatomy quiz game, mastery learning, NerdGames',
    ogImage: '/icons/icon-512.png',
  },

  '/games/home': {
    title: 'NerdGames — Free Learning Games for Students',
    description: 'Free leveled mastery games: master the periodic table, genetic code, anatomy, the unit circle, and number sense by depth — one stage at a time. No signup.',
    keywords: 'learning games, study games, free educational games, periodic table game, unit circle practice, anatomy quiz game, mastery learning, NerdGames',
    ogImage: '/icons/icon-512.png',
  },

  '/legal': {
    title: 'Privacy & Terms — NerdSuite',
    description: 'NerdSuite privacy policy and terms of use. No accounts, no individual tracking, aggregate pageview analytics only — tools run in your browser. Read the full policy.',
    keywords: 'NerdSuite privacy policy, terms of use, no individual tracking, student privacy, data policy, aggregate analytics',
    ogImage: '/icons/icon-512.png',
  },
  // V53 — NerdTools expansion (37 tools)
  // Level Pro (REPLACES /tools/level)
  '/tools/level': {
    title: 'Level Pro — Free Bubble, Slope, Plumb & Angle Tool | NerdTools',
    description: 'Six-mode digital level: bubble, two-axis, angle finder, slope/grade, plumb, and camera overlay. Free spirit level in your browser. No signup, no install.',
    keywords: 'bubble level, digital level, slope calculator, plumb level, angle finder, inclinometer, level app, phone level, free level tool',
    ogImage: '/icons/icon-512.png',
  },

  // Family 1 — Level & Measure
  '/tools/picture-hanging': {
    title: 'Picture-Hanging Guide — Free Nail Position Calculator | NerdTools',
    description: 'Plan even nail positions for hanging pictures on a wall. Inputs: wall width, picture width, count. Outputs: a printable list. Free, no signup.',
    keywords: 'picture hanging calculator, wall layout, nail position, hang pictures evenly, gallery wall, picture spacing, framing tool',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/object-height': {
    title: 'Object Height Estimator — Measure Trees & Buildings | NerdTools',
    description: 'Estimate height of trees, buildings, or anything tall from a known distance. Uses your phone camera and motion sensors. Free, no signup, runs locally.',
    keywords: 'tree height calculator, measure tall objects, building height estimator, trigonometry height, phone clinometer, surveyor tool',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/symmetry': {
    title: 'Symmetry Checker — Live Mirror Overlay Camera | NerdTools',
    description: 'Check how symmetric your art, Lego build, or any object is. Live camera mirrors one half over the other so you can see asymmetry. Free, runs locally.',
    keywords: 'symmetry checker, mirror camera, asymmetry detector, art symmetry tool, lego symmetry, drawing symmetry guide',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/photo-straightener': {
    title: 'Photo Straightener — Free Online Rotate & Crop | NerdTools',
    description: 'Upload a tilted photo, drag a slider to straighten it, download the cropped result. No upload to a server — processing happens in your browser.',
    keywords: 'photo straightener, rotate image online, fix tilted photo, photo crop tool, image rotation, straighten photo free',
    ogImage: '/icons/icon-512.png',
  },

  // Family 2 — Visual & Camera
  '/tools/magnifier': {
    title: 'Magnifier — Free Digital Magnifying Glass | NerdTools',
    description: 'Camera magnifier with high zoom, flashlight, freeze-frame, and contrast. Read tiny text, examine details, thread needles. Free, no signup.',
    keywords: 'magnifying glass app, phone magnifier, digital loupe, reading magnifier, tiny text reader, contrast camera',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/color-picker': {
    title: 'Color Picker — Free HEX, RGB, HSL Identifier | NerdTools',
    description: 'Aim your camera at any color and tap to sample it. Get HEX, RGB, and HSL values. Useful for paint matching, design, photography. Free.',
    keywords: 'color picker, paint matcher, hex color finder, RGB picker, HSL picker, design color tool, camera color identifier',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/brightness': {
    title: 'Brightness Meter — Free Approximate Light Meter | NerdTools',
    description: 'Approximate ambient brightness from camera input. Labeled zones (dim, indoor, bright, direct sun) plus EV reading. Honest about precision.',
    keywords: 'light meter, brightness meter, EV calculator, ambient light, room lighting check, photography light meter',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/grid-camera': {
    title: 'Grid Overlay Camera — Composition Guide | NerdTools',
    description: 'Live camera with rule-of-thirds, golden ratio, fibonacci spiral, and custom grids. Compose better shots; capture with your native camera app.',
    keywords: 'rule of thirds, composition grid, golden ratio camera, fibonacci spiral overlay, photography grid, viewfinder grid',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/before-after': {
    title: 'Before/After Overlay — Photo Comparison Slider | NerdTools',
    description: 'Upload two photos, drag a divider to compare. Renovation progress, plant growth, art progress, cleaning. Free, runs locally in your browser.',
    keywords: 'before after slider, photo comparison, side by side photos, renovation progress photos, comparison overlay',
    ogImage: '/icons/icon-512.png',
  },

  // Family 3 — Sound & Audio
  '/tools/tone': {
    title: 'Tone Generator — Free Sine, Square, Triangle Wave | NerdTools',
    description: 'Generate continuous waveforms 20 Hz – 20 kHz. Sine, square, triangle, sawtooth. Useful for hearing tests, instrument tuning, troubleshooting electronics.',
    keywords: 'tone generator, frequency generator, sine wave, square wave, audio test tone, hearing test frequencies, oscillator',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/tuning-fork': {
    title: 'Tuning Fork — A=440, A=432, Guitar, Chromatic | NerdTools',
    description: 'Reference pitches for tuning: A=440, A=432, A=415 baroque, chromatic, guitar EADGBE, bass, violin. Free online tuning fork — no signup.',
    keywords: 'tuning fork online, A440 Hz, A432 Hz, guitar tuner reference, chromatic tuner, instrument tuning, pitch reference',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/metronome': {
    title: 'Metronome — Free BPM Click Track with Tap Tempo | NerdTools',
    description: 'Accurate metronome 40-240 BPM with time signatures (4/4, 3/4, 6/8, etc.), tap tempo, downbeat accent. Uses Web Audio for drift-free timing.',
    keywords: 'metronome online, free metronome, BPM counter, tap tempo, click track, music practice, time signature metronome',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/spectrogram': {
    title: 'Spectrogram — Real-Time Audio Frequency Visualizer | NerdTools',
    description: 'Beautiful real-time spectrogram of your microphone input. See frequencies in speech, music, and environmental sounds. Audio analyzed locally.',
    keywords: 'spectrogram, audio analyzer, frequency visualizer, FFT audio, waterfall display, speech spectrum, music spectrum',
    ogImage: '/icons/icon-512.png',
  },

  // Family 4 — Outdoor & Navigation
  '/tools/gps': {
    title: 'GPS Coordinates — Free Lat/Long Reader | NerdTools',
    description: 'Read your current latitude and longitude in decimal and degrees/minutes/seconds. Copy in one tap, share via maps link. Free, location stays local.',
    keywords: 'GPS coordinates, latitude longitude, DMS coordinates, share my location, lat long reader, current GPS',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/altimeter': {
    title: 'Altimeter — Free GPS Elevation Reader | NerdTools',
    description: 'GPS-based elevation reader with baseline tracking for hiking gain/loss. Honestly GPS-only — browsers do not expose barometric sensors.',
    keywords: 'altimeter app, elevation reader, hiking gain loss, GPS altitude, current elevation, altitude meter',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/sun-moon': {
    title: 'Sun & Moon Direction — Photography & Hiking | NerdTools',
    description: 'Where the sun and moon are in the sky right now, from your location. Golden hour direction for photography, daylight planning for gardening, sky orientation for hikers. Compass-style display with optional live heading overlay. Free, no signup.',
    keywords: 'sun position, moon position, golden hour direction, sun azimuth, moon azimuth, sun angle calculator, photographer sun tool, gardening sun tracker, where is the sun now',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/trail-marker': {
    title: 'Trail Marker — Return-to-Point GPS Tool | NerdTools',
    description: 'Mark your car, campsite, or trailhead. Get live distance and bearing back. Hiking off-trail, parking lots, festivals, meetups. Mark persists this session only — GPS accuracy noted.',
    keywords: 'return to point, find my car, GPS marker, bearing to point, trail return, parking lot finder, festival meetup, off trail navigation',
    ogImage: '/icons/icon-512.png',
  },

  // Family 5 — DIY & Workshop
  '/tools/metal-detector': {
    title: 'Metal Detector — Magnetometer Ferrous Finder | NerdTools',
    description: 'Detects ferrous metal (nails, screws, conduit) via your phone magnetometer. Will not find aluminum studs or wood-only construction. For critical work use a real stud finder.',
    keywords: 'metal detector phone, magnetometer detector, ferrous metal finder, stud finder phone, find screws in wall, find conduit',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/square-checker': {
    title: 'Square Checker — Free 90° Corner Test | NerdTools',
    description: 'Check if a corner is exactly 90°. Place phone on one surface, set reference, rotate to perpendicular to read the angle. Free, no signup.',
    keywords: 'square checker, 90 degree test, corner square test, framing square, perpendicular checker, woodworking square',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/drill-bit': {
    title: 'Drill Bit Guide — Pilot & Clearance Holes | NerdTools',
    description: 'Pick screw size and material; get pilot drill and clearance bit sizes. Imperial #4-#14, metric M3-M10, metal tap drills, drywall. Free, no signup.',
    keywords: 'drill bit size chart, pilot hole calculator, screw size drill bit, tap drill chart, woodworking drill guide, metric drill bit chart',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/roof-pitch': {
    title: 'Roof Pitch Calculator — Degrees, Slope, Rise/12 | NerdTools',
    description: 'Measure roof pitch in degrees, percent slope, and rise/12-run pitch fraction. Phone against rafter or shingle. Free roof inclinometer.',
    keywords: 'roof pitch calculator, roof slope, rise over run, roof angle, pitch fraction, 6/12 pitch, roofing calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/coverage': {
    title: 'Coverage Calculator — Paint, Concrete, Tile, Mulch | NerdTools',
    description: 'How many bags, gallons, or boxes do you need? Multi-material calculator with waste factor: concrete, paint, tile, mulch, gravel, lumber.',
    keywords: 'concrete calculator, paint coverage calculator, tile calculator, mulch calculator, gravel calculator, board feet calculator, material estimator',
    ogImage: '/icons/icon-512.png',
  },

  // Family 6 — Math & Engineering
  '/tools/fraction': {
    title: 'Fraction Calculator — Mixed Number Math | NerdTools',
    description: 'Add, subtract, multiply, divide mixed fractions. Result shown as improper fraction, mixed number, and decimal. Mobile-friendly input.',
    keywords: 'fraction calculator, mixed number calculator, fraction math, fraction add subtract multiply, improper fraction converter',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/right-triangle': {
    title: 'Right Triangle Calculator — Sides, Angles, Area | NerdTools',
    description: 'Enter any 2 of 6 properties; rest fill in automatically. Side a, b, hypotenuse c, angles A, B (C=90°). Includes area. Free, no signup.',
    keywords: 'right triangle calculator, hypotenuse calculator, pythagorean theorem, sin cos tan calculator, triangle solver, trig calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/resistor': {
    title: 'Resistor Color Code Calculator — 4 & 5 Band | NerdTools',
    description: 'Bidirectional resistor color code reader. Pick colors → get resistance, or enter value → get band colors. Visual resistor preview. Free.',
    keywords: 'resistor color code, resistor calculator, ohm color chart, 4 band resistor, 5 band resistor, resistance value calculator',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/torque': {
    title: 'Bolt Torque Lookup — Imperial & Metric Bolts | NerdTools',
    description: 'Recommended tightening torque for plain dry threads. SAE Grade 2/5/8, ISO 8.8/10.9/12.9, A2-70 stainless. Foot-pounds and Newton-meters.',
    keywords: 'bolt torque chart, torque specifications, SAE grade torque, metric bolt torque, ISO 10.9 torque, A2-70 stainless torque',
    ogImage: '/icons/icon-512.png',
  },

  // Family 7 — Time & Counting
  '/tools/interval': {
    title: 'Interval Timer — Free HIIT & Tabata Timer | NerdTools',
    description: 'Configurable work/rest intervals with audio cues. HIIT, Tabata, cooking, study sprints. Drift-free Web Audio timing, screen wake-lock.',
    keywords: 'interval timer, HIIT timer, tabata timer, workout timer, custom interval timer, multi step timer',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/tally': {
    title: 'Tally Counter — Multi-Channel Counting App | NerdTools',
    description: 'Up to 10 renamable counters with +/- buttons. Bird-watching, inventory, attendance, anything. CSV export. Refresh resets — no signup, no tracking.',
    keywords: 'tally counter, multi counter, bird watching counter, inventory counter, click counter, counter app',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/reaction': {
    title: 'Reaction Time Tester — Free Reflex Test | NerdTools',
    description: 'Wait for the green flash and lift your finger as fast as you can. 5 trials with best, average, worst. Pure JS, no sensors. Free, no signup.',
    keywords: 'reaction time test, reflex test, response time, reaction speed test, brain reflex test, reaction time game',
    ogImage: '/icons/icon-512.png',
  },

  // Family 8 — Sensor Lab
  '/tools/vibration': {
    title: 'Vibration Meter / Seismograph — Live Accelerometer | NerdTools',
    description: 'Real-time accelerometer waveform on three axes plus magnitude. Find loose washing machines, vibrating tools, or play seismograph. 10-second scrolling window with peak-hold. Sensors stay on your device.',
    keywords: 'vibration meter, seismograph app, accelerometer visualizer, vibration tester, hand tool vibration, washing machine balance, science demo',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/magnetic-field': {
    title: 'Magnetic Field Visualizer — Magnetometer Tool | NerdTools',
    description: 'Visualize the magnetic field around you. Find hidden cables, locate speakers behind walls, science demos. Earth-field baseline (~50 μT) shown with deviation tracking.',
    keywords: 'magnetic field meter, magnetometer visualizer, find hidden cables, locate speakers, science demo magnet, earth magnetic field',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/steadiness': {
    title: 'Hand Steadiness Tester — 10-Second Stillness Score | NerdTools',
    description: 'Hold your phone as still as you can for 10 seconds. Tool measures total movement and gives a steadiness score. Not a medical device — see a doctor for tremor concerns. Free, runs locally.',
    keywords: 'hand steadiness test, stillness test, phone tremor test, motion test, mindfulness focus test',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/shake-counter': {
    title: 'Shake & Rotation Counter — Free Motion Counter | NerdTools',
    description: 'Counts sharp shakes (paint cans, "shake 100 times") and full rotations (gyroscope-integrated). Two counters in parallel with axis selection.',
    keywords: 'shake counter, rotation counter, paint can counter, exercise rep counter, motion counter, gyroscope counter',
    ogImage: '/icons/icon-512.png',
  },

  // Family 9 — Practical / Generators
  '/tools/currency': {
    title: 'Currency Converter — Daily Rates, Honest Staleness | NerdTools',
    description: 'Convert between 24 major currencies. Rates cached daily; "OFFLINE" badge when older than 24 hours. Honest about precision. Free, no signup.',
    keywords: 'currency converter, USD to EUR, exchange rate, currency exchange, free currency converter, mid market rate',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/password': {
    title: 'Password Generator — Strong, Crypto-Random | NerdTools',
    description: 'Cryptographic-quality passwords with entropy bits shown. Length 8-64, character class toggles, exclude ambiguous (0/O, 1/l/I). Uses crypto.getRandomValues. Free.',
    keywords: 'password generator, strong password, secure password, random password, password entropy, crypto.getRandomValues',
    ogImage: '/icons/icon-512.png',
  },
  '/tools/exif': {
    title: 'EXIF Remover — Strip Photo Metadata Locally | NerdTools',
    description: 'Strips GPS location, camera model, timestamps, and other EXIF/IPTC/XMP metadata from JPEG and PNG. Shows what was found before stripping. Your file never leaves your device — all processing local. Free, no signup.',
    keywords: 'EXIF remover, strip GPS from photo, remove photo metadata, clean photo before sharing, photo privacy, metadata stripper, JPEG metadata, PNG metadata',
    ogImage: '/icons/icon-512.png',
  },
};

export function getSeo(path) {
  return SEO[path] || SEO['/'];
}

export { SITE_URL };
